import { gql, useMutation, useQuery } from '@apollo/client';
import { Spinner } from '@apollo/orbit';
import * as Sentry from '@sentry/react';
import { useFormik } from 'formik';
import { AnimatePresence } from 'framer-motion';
import { GraphQLError } from 'graphql';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Prompt, useHistory, useLocation } from 'react-router';
import { Wizard } from 'react-use-wizard';
import * as Yup from 'yup';

import * as orgRoutes from 'src/app/account/routes';
import {
  HeaderEntry,
  INITIAL_HEADERS,
} from 'src/app/graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/shared';
import {
  getGraphIdentifierFromGraphRef,
  usePerGraphIdentifierLocalStorage,
} from 'src/app/graph/explorerPage/hooks/usePerGraphIdentifierLocalStorage';
import { GraphVariantsSettingsPageQuery } from 'src/app/graph/graphSettingsPage/variantsPage/SourceVariantsPage';
import { parseGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { graphSettingsRouteConfig } from 'src/app/graph/routes';
import { AddSubgraphFormMutation } from 'src/app/graph/shared/addSubgraphForm/AddSubgraphForm';
import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { ErrorPage } from 'src/components/errorPage/ErrorPage';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCheckPlanTypes } from 'src/hooks/useCheckPlanTypes';
import { useCreateCloudOnboardingMutation } from 'src/hooks/useCloudOnboarding';
import {
  usePrivateSubgraphSyncMutation,
  usePrivateSubgraphsQuery,
} from 'src/hooks/useCloudResourceManagement';
import { useCreateSupergraph } from 'src/hooks/useCreateSupergraph';
import { usePrivateSharedResourcesQuery } from 'src/hooks/usePrivateSharedResourcesQuery';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useShowCloudRouterEndpointStatusToast } from 'src/hooks/useShowCloudRouterStatusToast';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import {
  ignorePermissionsErrors,
  isInternalServerError,
} from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { DEFAULT_VARIANT_NAME } from 'src/lib/graphOnboarding/constants';
import { generateUniqueGraphName } from 'src/lib/graphOnboarding/helpers/utils';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  CloudProvider,
  CloudTier,
  GraphState,
  GraphType,
} from 'src/lib/graphqlTypes/types';
import { isLocalUrl } from 'src/lib/isLocalUrl';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { FlowTypeContext } from './FlowTypeProvider';
import { extractErrorMessage } from './helpers/extractErrorMessage';
import { ButtonState } from './NewSupergraphOnboardingControls';
import { NewSupergraphOnboardingFooter } from './NewSupergraphOnboardingFooter';
import { PrivateSupergraphSummaryStep } from './privateSupergraphSummaryStep/PrivateSupergraphSummaryStep';
import { SelectDedicatedPlanStep } from './selectDedicatedPlanStep/SelectDedicatedPlanStep';
import { SetupEndpointorPrivateConnectionStep } from './setupEndpointorPrivateConnectionStep/SetupEndpointorPrivateConnectionStep';
import { SetupPrivateGraphQLEndpointStep } from './setupPrivateGraphQLEndpointStep/SetupPrivateGraphQLEndpointStep';
import {
  CommonCloudDedicatedProps,
  CommonStepProps,
  OnboardingLayoutProps,
  SupergraphOnboardingStep,
  UrlInfo,
  getSubgraphNameFromGraphVariant,
  newSupergraphOnboardingValidationSchema,
} from './shared';
import { SupergraphStep } from './supergraphStep/SupergraphStep';
import { YourGraphQLAPIStep } from './yourGraphQLApiStep/YourGraphQLAPIStep';

type FormValues = Yup.InferType<typeof newSupergraphOnboardingValidationSchema>;

export const NewSupergraphOnboarding = () => {
  const history = useHistory();
  const location = useLocation();

  const { triggerRefetch: triggerRefetchOfCloudRouterStatus } =
    useShowCloudRouterEndpointStatusToast();
  const { orgId, formValues, includeVariantOnboardingStep, prefillGraphRef } =
    useRouteParams(orgRoutes.supergraphOnboarding);
  const currentPlanResult = useCurrentPlan({
    skip: !orgId,
    accountId: orgId as string,
  });
  const { isDedicatedPlan } = useCheckPlanTypes({
    tier: currentPlanResult?.currentPlan?.tier,
    kind: currentPlanResult?.currentPlan?.kind,
  });

  // If we are in the variant onboarding flow, we need to set the default
  // graphId to the graphId of the graph we are creating a variant for.
  const graphRef = parseGraphRef(prefillGraphRef);

  const isVariantOnboardingFlow = includeVariantOnboardingStep === 'true';

  const { data, loading: dataLoading } = useQuery<
    GraphQLTypes.NewSupergraphOnboardingQuery,
    GraphQLTypes.NewSupergraphOnboardingQueryVariables
  >(
    gql`
      query NewSupergraphOnboardingQuery(
        $orgId: ID!
        $filterBy: GraphFilter
        $includeDeleted: Boolean
      ) {
        account(id: $orgId) {
          id
          name
          graphs(filterBy: $filterBy, includeDeleted: $includeDeleted) {
            id
            graphType
            title
            variants {
              id
              name
              hasManagedSubgraphs
              url
              activeSchemaPublish {
                id
              }
              router {
                id
                shard {
                  id
                  region {
                    code
                    name
                  }
                }
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        orgId,
        filterBy: {
          state: GraphState.CONFIGURED,
          type: [GraphType.CLASSIC, GraphType.CLOUD_SUPERGRAPH],
        },
      },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const [previousStep, setPreviousStep] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [graphCreationLoading, setGraphCreationLoading] = useState(false);
  const [createDedicatedVariantError, setCreateDedicatedVariantError] =
    useState({
      message: '',
    });

  const [hasPrivateSubgraphs, setHasPrivateSubgraphs] = useState(false);
  const [privateSupergraphSummaryData, setPrivateSupergraphsData] = useState<{
    serviceId: string;
    variant: string;
    name: string;
    url: string | null;
  }>({
    serviceId: '',
    variant: '',
    name: '',
    url: '',
  });

  const [availableUnregisteredResources, setAvailableUnregisteredResources] =
    useState<UrlInfo[]>([]);

  const [selectedPrivateDomainName, setSelectedPrivateDomainName] =
    useState('');

  useEffect(() => {
    setLoading(dataLoading);
  }, [dataLoading]);

  const { flowType } = useContext(FlowTypeContext);

  const defaultSupergraphTitle = useMemo(() => {
    const existingGraphTitles = data?.account?.graphs.map(({ title }) => title);

    return generateUniqueGraphName(data?.account?.name, existingGraphTitles);
  }, [data]);

  const variantsToCreateSupergraphFrom = useMemo(
    () =>
      data?.account?.graphs.reduce<
        Array<
          GraphQLTypes.NewSupergraphOnboardingQuery_account_graphs_variants & {
            graph: { id: string; title: string };
          }
        >
      >((accumulator, graph) => {
        return accumulator.concat(
          graph.variants
            .filter(
              (variant) =>
                !variant.hasManagedSubgraphs &&
                !!variant.activeSchemaPublish &&
                !!variant.url &&
                !isLocalUrl(variant.url),
            )
            .map((variant) => ({ ...variant, graph })),
        );
      }, []),
    [data],
  );

  const existingGraphVariants = useMemo(() => {
    if (!loading && data?.account?.graphs) {
      return data.account.graphs
        .filter(
          (graph) =>
            graph.graphType === GraphType.CLOUD_SUPERGRAPH &&
            graph.id === graphRef?.graphId,
        )
        .flatMap((graph) =>
          graph.variants.map((variant) => ({
            graphVariantRef: variant.id,
            graphName: graph.title,

            variantName: variant.name,
            cloudRegion: {
              code: variant.router?.shard?.region.code ?? '',
              name: variant.router?.shard?.region.name ?? '',
            },
          })),
        );
    }
    return [];
  }, [data?.account?.graphs, graphRef?.graphId, loading]);

  const shouldSeeTabbedView =
    variantsToCreateSupergraphFrom && variantsToCreateSupergraphFrom.length > 0;

  const trackCustomEvent = useTrackCustomEvent();

  const [createGraph, { data: createGraphResponse, error: createGraphError }] =
    useCreateSupergraph();

  // Initialize the mutation for creating a new cloud onboarding process.
  // createCloudOnboarding: Function to execute the mutation.
  // createCloudOnboardingError: Any error that occurs during the mutation.
  const { createCloudOnboarding, error: createCloudOnboardingError } =
    useCreateCloudOnboardingMutation();

  // Initialize the mutation for syncing private subgraphs with the server.
  // sharedResources: The list of available resources to onboard as subgraphs.
  const {
    data: sharedResources,
    loading: sharedResourcesLoading,
    error: sharedResourcesError,
  } = usePrivateSubgraphSyncMutation();

  const {
    getPrivateSubgraphs,
    data: privateSubgraphsData,
    loading: privateSubgraphsLoading,
    error: privateSubgraphsError,
  } = usePrivateSubgraphsQuery(orgId, CloudProvider.AWS);

  // Manage private subgraphs by combining multiple hooks to fetch and control their state.
  // setShouldQueryPrivateSubgraphs: Function to trigger the query for private subgraphs.
  // managedPrivateSubgraphsData: The list of shared resources available to onboard as subgraphs.
  const { setShouldQueryPrivateSubgraphs } = usePrivateSharedResourcesQuery({
    getPrivateSubgraphs,
    privateSubgraphsError,
    privateSubgraphsData,
    privateSubgraphsLoading,
    availableUnregisteredResources,
    setAvailableUnregisteredResources,
  });

  const createDedicatedSupergraph = async (submittedFormValues: FormValues) => {
    setHasSubmitted(true);

    try {
      const createCloudOnboardingResult = await createCloudOnboarding({
        accountId: orgId,
        cloudOnboardingInput: {
          graphRef: `${submittedFormValues?.supergraphID}@${DEFAULT_VARIANT_NAME}`,
          provider: CloudProvider.AWS,
          region: submittedFormValues.cloudRegion ?? 'us-east-1',
          tier: submittedFormValues?.cloudTier ?? CloudTier.DEDICATED,
        },
      });
      // Here we check if there are any errors from the server in the mutation result
      // We can directly access the createGraphResult.data to access the __typename
      if (
        createCloudOnboardingResult.data?.account?.createCloudOnboarding
          .__typename === 'CreateOnboardingSuccess'
      ) {
        createServerlessSupergraph(submittedFormValues);
      }
    } catch {
      // we handle the error in the useMutation above
    }
  };

  const createServerlessSupergraph = async (
    submittedFormValues: FormValues,
  ) => {
    setHasSubmitted(true);
    setGraphCreationLoading(true);
    const unencodedRoutingUrl = (
      submittedFormValues.unregisteredEndpointUrl.length > 0
        ? submittedFormValues.unregisteredEndpointUrl
        : submittedFormValues.registeredEndpointUrl
    ).trim();
    const encodedRoutingUrl = new URL(unencodedRoutingUrl).toString();
    try {
      const createGraphResult = await createGraph({
        accountId: orgId,
        graphType: GraphQLTypes.GraphType.CLOUD_SUPERGRAPH,
        hiddenFromUninvitedNonAdmin: false,
        graphId: submittedFormValues.supergraphID,
        title: submittedFormValues.supergraphName,
        variantCreationConfig: {
          variantName: DEFAULT_VARIANT_NAME,
          buildConfigInput: {
            apiDirectivePassThrough: [],
            buildPipelineTrack: null,
            composition: {
              subgraphs: [
                {
                  document: submittedFormValues.sdl,
                  name:
                    submittedFormValues.unregisteredSubgraphName.length > 0
                      ? submittedFormValues.unregisteredSubgraphName
                      : submittedFormValues.registeredVariantSubgraphName,
                  routingURL: encodedRoutingUrl,
                },
              ],
            },
          },
        },
      });

      if (!createGraphResult?.errors) {
        // set initial headers in local storage, the first time this user
        // opens the explorer, these headers will be populated in their tab
        setInitialSupergraphCreationHeaders(headers);

        triggerRefetchOfCloudRouterStatus();

        const schemaSource =
          submittedFormValues.tab === 'registered'
            ? 'REG'
            : isUsingLocalIntrospection
              ? 'LOCAL'
              : schemaUploaded
                ? 'SDL'
                : 'INTRO';

        // fire ga4 event
        trackCustomEvent({
          action: 'successfully_created_supergraph',
          category: '6M2S: Onboarding',
          label: `hadRegGraphOption:${!!shouldSeeTabbedView},usedRegGraphOption:${
            submittedFormValues.tab === 'registered'
          }`,
          secondaryLabel: `schemaSource:${schemaSource}`,
          graphId: submittedFormValues.supergraphID,
          graphVariant: DEFAULT_VARIANT_NAME,
        });

        const graphData = createGraphResult?.data?.account?.createGraph;

        if (
          flowType === 'DEDICATED_PRIVATE' &&
          graphData?.__typename === 'Service' &&
          graphData?.variants[0]?.__typename === 'GraphVariant'
        ) {
          setPrivateSupergraphsData({
            serviceId: submittedFormValues?.supergraphID,
            variant: DEFAULT_VARIANT_NAME,
            name: graphData.variants[0].name,
            url: graphData.variants[0].url,
          });
        } else {
          history.push({
            ...setOverlayVisibleLocation({
              location: {
                ...orgRoutes.graphs.location({ orgId }),
                state: {
                  initiatingSupergraphState: {
                    graphId: submittedFormValues.supergraphID,
                    graphVariant: DEFAULT_VARIANT_NAME,
                  },
                },
              },
              overlayName: Config.modals.initiatingCloudEndpoint,
            }),
          });
        }
      }
    } catch {
      // we handle the error in the useMutation above
    } finally {
      setGraphCreationLoading(false);
    }
  };

  const createSupergraph = async (submittedFormValues: FormValues) => {
    if (isDedicatedPlan) {
      await createDedicatedSupergraph(submittedFormValues);
    } else {
      await createServerlessSupergraph(submittedFormValues);
    }
  };

  const [addSubgraph] = useMutation<
    GraphQLTypes.AddSubgraphFormMutation,
    GraphQLTypes.AddSubgraphFormMutationVariables
  >(AddSubgraphFormMutation, {
    awaitRefetchQueries: true,
    refetchQueries: (mutationResult) => {
      if (mutationResult.data?.service?.publishSubgraph?.graphCompositionID) {
        return [
          {
            query: GraphVariantsSettingsPageQuery,
            variables: {
              graphId:
                mutationResult.data.service.publishSubgraph.graphCompositionID,
            },
          },
        ];
      }
      return [];
    },
  });

  const createDedicatedVariant = async (submittedFormValues: FormValues) => {
    setHasSubmitted(true);
    setGraphCreationLoading(true);

    try {
      const createCloudOnboardingResult = await createCloudOnboarding({
        accountId: orgId,
        cloudOnboardingInput: {
          graphRef:
            graphRef?.graphId && submittedFormValues.variantName
              ? `${graphRef.graphId}@${submittedFormValues.variantName}`
              : '',
          provider: CloudProvider.AWS,
          region: submittedFormValues.cloudRegion ?? '',
          tier: submittedFormValues.cloudTier ?? CloudTier.DEDICATED,
        },
      });

      // Check for errors in createCloudOnboardingResult
      const onboardingResultType =
        createCloudOnboardingResult?.data?.account?.createCloudOnboarding
          .__typename;

      if (onboardingResultType === 'InvalidInputErrors') {
        throw new Error(
          createCloudOnboardingResult?.data?.account?.createCloudOnboarding
            ?.message ?? 'Invalid input errors',
        );
      } else if (onboardingResultType === 'InternalServerError') {
        throw new Error(
          createCloudOnboardingResult?.data?.account?.createCloudOnboarding
            .message ?? 'Internal server error',
        );
      }

      // Ensure to continue only if the onboarding was successful
      if (
        createCloudOnboardingResult.data?.account?.createCloudOnboarding
          .__typename !== 'CreateOnboardingSuccess'
      ) {
        throw new Error('Cloud onboarding was not successful');
      }

      const unencodedRoutingUrl =
        submittedFormValues.unregisteredEndpointUrl.trim();
      const encodedRoutingUrl = new URL(unencodedRoutingUrl).toString();

      const createDedicatedVariantResult = await addSubgraph({
        variables: {
          graphId: graphRef?.graphId ?? '',
          variant: submittedFormValues.variantName,
          subgraphName: submittedFormValues.unregisteredSubgraphName ?? '',
          routingUrl: encodedRoutingUrl,
          revision: `studio-${orgId}-${Date.now()}`,
          partialSchema: {
            sdl: submittedFormValues.sdl,
          },
        },
      });

      // Check for errors in createDedicatedVariantResult
      const subgraphResult =
        createDedicatedVariantResult?.data?.service?.publishSubgraph;
      if (
        createDedicatedVariantResult?.data?.service === null ||
        (subgraphResult &&
          subgraphResult.__typename === 'CompositionAndUpsertResult' &&
          subgraphResult.errors.length > 0)
      ) {
        const errors = subgraphResult?.errors
          .filter((err) => err)
          .map((err) => err?.message)
          .join(', ');
        throw new Error(
          errors || 'We could not create your dedicated variant at this time.',
        );
      }

      // Continue if there are no errors
      if (createDedicatedVariantResult?.data?.service?.publishSubgraph) {
        history.push(
          graphSettingsRouteConfig.path({
            graphId: graphRef?.graphId ?? '',
            graphVariant: submittedFormValues.variantName,
            tab: 'variants',
          }),
        );
      }
    } catch (e) {
      if (isInternalServerError(e as GraphQLError)) {
        Sentry.captureException(e);
      } else {
        // Handle other errors
        const errorMessage = extractErrorMessage(e);
        setCreateDedicatedVariantError({ message: errorMessage });
        Sentry.captureException(e);
      }
    } finally {
      setGraphCreationLoading(false);
    }
  };

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    validateField,
    setValues,
    dirty,
  } = useFormik<FormValues>({
    validateOnBlur: true,
    validateOnChange: false,
    initialValues: {
      tab: formValues?.tab ?? 'unregistered',
      registeredGraphRef:
        formValues?.registeredGraphRef ??
        ((!isVariantOnboardingFlow && prefillGraphRef) || ''),
      registeredVariantSubgraphName:
        formValues?.registeredVariantSubgraphName ?? '',
      registeredEndpointUrl: formValues?.registeredEndpointUrl ?? '',
      unregisteredEndpointUrl: formValues?.unregisteredEndpointUrl ?? '',
      unregisteredSubgraphName: formValues?.unregisteredSubgraphName ?? '',
      cloudProvider: formValues?.cloudProvider ?? CloudProvider.AWS,
      cloudTier: CloudTier.DEDICATED,
      cloudRegion: formValues?.cloudRegion ?? 'us-east-1',
      arn: formValues?.arn ?? '',
      // This sdl is the introspected document, manual sdl upload or sdl from the registry.
      // For a subgraph, this will include all federation directives
      sdl: formValues?.sdl ?? '',
      supergraphName: formValues?.supergraphName ?? '',
      supergraphID: formValues?.supergraphID ?? '',
      variantName: formValues?.variantName ?? '',
    },
    onSubmit: async (submittedFormValues) =>
      isVariantOnboardingFlow
        ? createDedicatedVariant(submittedFormValues)
        : createSupergraph(submittedFormValues),
    validationSchema: newSupergraphOnboardingValidationSchema,
  });

  const [__, setInitialSupergraphCreationHeaders] =
    usePerGraphIdentifierLocalStorage({
      graphIdentifier: getGraphIdentifierFromGraphRef({
        graphId: values.supergraphID,
        graphVariant: DEFAULT_VARIANT_NAME,
      }),
      key: 'initialSupergraphCreationHeaders',
      stableInitialValue: [],
    });

  const prefillGraphRefApiName = useMemo(() => {
    const graphTitle = variantsToCreateSupergraphFrom?.find(
      (variant) => variant.id === prefillGraphRef,
    )?.graph.title;
    if (graphRef && graphTitle) {
      return getSubgraphNameFromGraphVariant({
        graphTitle,
        variantName: graphRef.graphVariant,
      });
    }
  }, [graphRef, prefillGraphRef, variantsToCreateSupergraphFrom]);

  /**
   * The below three useEffects are necessary to update our state when data loads.
   * We need to use an effect to update these because the initialValues won't reinitialize
   * in formik. We have enableReinitialize: false which is the default, so we
   * can set the url state formValues as the initial values.
   */
  useEffect(() => {
    if (prefillGraphRefApiName) {
      setFieldValue('registeredVariantSubgraphName', prefillGraphRefApiName);
    }
  }, [prefillGraphRefApiName, setFieldValue]);
  useEffect(() => {
    if (
      shouldSeeTabbedView &&
      !(formValues && formValues.tab === 'unregistered')
    ) {
      setFieldValue('tab', 'registered');
    }
  }, [formValues, setFieldValue, shouldSeeTabbedView]);
  useEffect(() => {
    setFieldValue('supergraphName', defaultSupergraphTitle);
  }, [defaultSupergraphTitle, setFieldValue]);

  // When we prefill the graphRef, we have to manually tell formik
  // to validate these fields, since we don't call the blur functions
  useEffect(() => {
    if (prefillGraphRef) {
      validateField('registeredVariantSubgraphName');
      validateField('registeredGraphRef');
      validateField('registeredEndpointUrl');
    }
  }, [prefillGraphRef, validateField]);

  // All of the below state is in this component rather than the UnregisteredGraphTabContent
  // component so it is saved when we navigate between steps
  const [schemaUploaded, setSchemaUploaded] = useState(false);
  const [isUsingLocalIntrospection, setIsUsingLocalIntrospection] =
    useState(false);
  const [headers, setHeaders] = useState<HeaderEntry[]>(INITIAL_HEADERS);
  const [advancedOptionsDrawerOpen, setAdvancedOptionsDrawerOpen] =
    useState(false);
  const unregisteredGraphTabState = useMemo(
    () => ({
      schemaUploaded,
      setSchemaUploaded,
      headers,
      setHeaders,
      advancedOptionsDrawerOpen,
      setAdvancedOptionsDrawerOpen,
    }),
    [advancedOptionsDrawerOpen, headers, schemaUploaded],
  );

  /**
   * GA Event tracking state
   */

  const [whenStepsBegan, setWhenStepsBegan] =
    useState<Partial<Record<SupergraphOnboardingStep, number>>>();
  const continueAnalyticsLabelsByStep = useMemo(() => {
    return {
      [SupergraphOnboardingStep.YourGraphQLAPI]: `hasRegGraphOption:${!!shouldSeeTabbedView},usedRegGraphOption:${
        values.tab === 'registered'
      },numberOfAddedHeaders:${
        headers.length
      },schemaUploaded:${!!schemaUploaded}`,
      [SupergraphOnboardingStep.Supergraph]: `modifiedSupergraphName:${
        defaultSupergraphTitle !== values.supergraphName
      }`,
    };
  }, [
    defaultSupergraphTitle,
    headers.length,
    schemaUploaded,
    shouldSeeTabbedView,
    values.supergraphName,
    values.tab,
  ]);

  /**
   * end GA Event tracking state
   */

  const commonCloudDedicatedProps: CommonCloudDedicatedProps = {
    isDedicatedPlan,
    flowType,
    sharedResources,
    sharedResourcesLoading,
    sharedResourcesError,
    availableUnregisteredResources,
    existingGraphVariants,
    prefillGraphRef,
    setAvailableUnregisteredResources,
    privateSubgraphsData,
    privateSubgraphsLoading,
    setShouldQueryPrivateSubgraphs,
    createCloudOnboardingError,
    setHasPrivateSubgraphs,
    selectedPrivateDomainName,
    setSelectedPrivateDomainName,
  };

  const onboardingLayoutProps: OnboardingLayoutProps = {
    previousStep,
    setPreviousStep,
    hasPrivateSubgraphs,
  };

  const commonStepProps: CommonStepProps = {
    values,
    errors,
    touched,
    handleBlur,
    setLoading,
    validateField,
    /** setValues & setFieldTouched both have a 3rd option for whether or not
     * to validate. setValues will validate the updated field,
     * setFieldTouched after a setFieldValue will validate the previous value.
     * We use setValues for immediate validation in our components.
     */
    setValues,
    setFieldValue,
    setFieldTouched,
    whenStepsBegan,
    setWhenStepsBegan,
    loading,
    creatingSupergraph: graphCreationLoading,
    continueAnalyticsLabelsByStep,
    createGraphError,
    createGraphValidationError:
      createGraphResponse?.account?.createGraph.__typename ===
      'GraphCreationError'
        ? createGraphResponse.account.createGraph.message
        : undefined,
    createSupergraph: handleSubmit,
  };

  const [createSupergraphButtonState, setCreateSupergraphButtonState] =
    useState<ButtonState>({
      isDisabled: false,
      disabledReason: undefined,
    });

  const [nextButtonState, setNextButtonState] = useState<ButtonState>({
    isDisabled: false,
    disabledReason: undefined,
  });

  if (dataLoading) {
    return (
      <div className="flex h-full items-center lg:my-44 lg:h-auto">
        <Spinner size="lg" className="mx-auto" />
      </div>
    );
  }

  if (!data?.account || !variantsToCreateSupergraphFrom) {
    return (
      <div className="flex h-full items-center">
        <ErrorMessage />
      </div>
    );
  }

  if (createDedicatedVariantError.message) {
    return (
      <ErrorPage>
        <div>{createDedicatedVariantError.message}</div>
        <ContactSupportLink className="text-error">
          Please contact support
        </ContactSupportLink>{' '}
        if you're stuck.
      </ErrorPage>
    );
  }

  const commonProps = {
    shouldSeeTabbedView: !!shouldSeeTabbedView,
    variantsToCreateSupergraphFrom,
    isUsingLocalIntrospection,
    setIsUsingLocalIntrospection,
    nextButtonState,
    ...unregisteredGraphTabState,
    ...commonStepProps,
    ...commonCloudDedicatedProps,
    ...onboardingLayoutProps,
  };

  const renderSteps = () => {
    switch (flowType) {
      case 'DEDICATED_PUBLIC':
        return {
          children: [
            <YourGraphQLAPIStep
              setNextButtonState={setNextButtonState}
              {...commonProps}
              key="your-graphql-api-step"
            />,
            <SelectDedicatedPlanStep
              setNextButtonState={setNextButtonState}
              {...commonProps}
              key="your-dedicated-plan-step"
            />,
            <SupergraphStep
              orgId={orgId}
              setNextButtonState={setCreateSupergraphButtonState}
              {...commonProps}
              key="supergraph-step"
            />,
          ],
        };
      case 'DEDICATED_PRIVATE':
        return {
          children: [
            <SelectDedicatedPlanStep
              setNextButtonState={setNextButtonState}
              {...commonProps}
              key="your-dedicated-plan-step"
            />,
            <SetupEndpointorPrivateConnectionStep
              setNextButtonState={setNextButtonState}
              {...commonProps}
              key="your-graphql-api-step"
            />,
            <SetupPrivateGraphQLEndpointStep
              setNextButtonState={setNextButtonState}
              {...commonProps}
              key="your-graphql-api-step"
            />,
            <SupergraphStep
              orgId={orgId}
              setNextButtonState={setCreateSupergraphButtonState}
              {...commonProps}
              key="supergraph-step"
            />,
            <PrivateSupergraphSummaryStep
              setNextButtonState={setNextButtonState}
              {...commonProps}
              data={
                privateSupergraphSummaryData && privateSupergraphSummaryData
              }
              key="summary-step"
            />,
          ],
        };
      case 'SERVERLESS':
      default:
        return {
          children: [
            <YourGraphQLAPIStep
              setNextButtonState={setNextButtonState}
              {...commonProps}
              key="your-graphql-api-step"
            />,
            <SupergraphStep
              orgId={orgId}
              setNextButtonState={setCreateSupergraphButtonState}
              {...commonProps}
              key="supergraph-step"
            />,
          ],
        };
    }
  };

  const renderWizard = ({ children }: { children: React.ReactNode }) => {
    return (
      <Wizard
        startIndex={formValues?.stepIndex ?? 0}
        footer={
          <NewSupergraphOnboardingFooter
            nextButtonState={nextButtonState}
            createSupergraphButtonState={createSupergraphButtonState}
            creatingSupergraph={graphCreationLoading}
            createSupergraph={handleSubmit}
            loading={loading}
            continueAnalyticsLabelsByStep={continueAnalyticsLabelsByStep}
            whenStepsBegan={whenStepsBegan}
          />
        }
      >
        {children}
      </Wizard>
    );
  };

  return (
    <div className="relative flex h-full flex-col items-center justify-between overflow-x-hidden bg-silver-light">
      <Prompt
        message={(redirectLocation) => {
          if (redirectLocation.pathname !== location.pathname) {
            return 'Are you sure you want to leave this page? Your progress will not be saved.';
          }
          return true;
        }}
        // on the last page, don't ask when we redirect for you to your new supergraph
        // or when we are in the variant onboarding flow
        when={dirty && !hasSubmitted && !isVariantOnboardingFlow}
      />
      <AnimatePresence initial={false} exitBeforeEnter>
        {renderWizard(renderSteps())}
      </AnimatePresence>
    </div>
  );
};
